import { ModuleContent, CaseStudy, Question } from './types';

export const MODULES: ModuleContent[] = [
  {
    id: 'virus-worm',
    title: 'Virus vs. Worm',
    definition: 'A Virus is malicious code that attaches to a host file (like a document). A Worm is a standalone program that self-replicates across networks.',
    entryVectors: 'Viruses arrive via infected downloads or attachments. Worms exploit network vulnerabilities (unpatched software) to jump from machine to machine.',
    objectives: 'To spread widely, corrupt files, damage system integrity, or consume network bandwidth (worms).',
    warningSigns: [
      'Computer running slower than usual',
      'Files disappearing or becoming corrupted',
      'Programs crashing or freezing often'
    ],
    prevention: [
      'Install and update Antivirus software',
      'Keep your Operating System (OS) patched',
      'Do not open attachments from unknown senders',
      'Use a firewall to block unauthorized connections',
      'Scan USB drives before opening files'
    ],
    detection: [
      'Run a full system Antivirus scan',
      'Monitor for high network usage (slow internet)',
      'Check for unexpected processes in Task Manager'
    ],
    response: [
      'Disconnect from the network (Wi-Fi/Cable) immediately',
      'Run a full Antivirus scan and quarantine threats',
      'Check other devices on the network',
      'Restore corrupted files from a clean backup',
      'Change passwords after cleaning the infection'
    ],
    myth: {
      myth: "I need to open an email attachment to get a worm.",
      reality: "Worms can travel automatically across a network without you clicking anything if your system is unpatched."
    },
    classMode: {
      duration: '10 mins',
      discussion: ['Why is a worm potentially more dangerous to a school network than a virus?', 'Can a virus exist without a file?']
    },
    quiz: [
      { id: 'q1', text: 'Which requires a host file to run?', options: ['Worm', 'Virus', 'Both', 'Neither'], correctIndex: 1, explanation: 'A virus needs a host file (like a document or program) to execute.' },
      { id: 'q2', text: 'What is a primary characteristic of a worm?', options: ['Encrypts files', 'Self-replicates across networks', 'Steals passwords only', 'Displays ads'], correctIndex: 1, explanation: 'Worms are designed to spread automatically across networks.' },
      { id: 'q3', text: 'Which impact is most common for network worms?', options: ['Hardware melting', 'Network congestion', 'Keyboard failure', 'Screen cracking'], correctIndex: 1, explanation: 'The replication traffic often clogs network bandwidth.' }
    ]
  },
  {
    id: 'trojan',
    title: 'Trojan Horse',
    definition: 'Malware that disguises itself as legitimate software (like a game or tool) to trick users into installing it.',
    entryVectors: 'Social engineering: Fake software downloads, pirated games/movies, or phishing emails pretending to be invoices.',
    objectives: 'To create a backdoor for hackers, steal sensitive data, or install other malware.',
    warningSigns: [
      'New desktop icons you didn\'t install',
      'Default browser homepage changed',
      'Antivirus randomly disabled'
    ],
    prevention: [
      'Download software only from official sources',
      'Avoid cracked/pirated software and keygens',
      'Enable "Show file extensions" (e.g., spot invoice.exe)',
      'Be skeptical of "too good to be true" free offers',
      'Keep User Account Control (UAC) enabled'
    ],
    detection: [
      'Antivirus real-time protection alerts',
      'Unexpected open ports on your firewall',
      'Programs starting automatically on boot'
    ],
    response: [
      'Disconnect from the internet',
      'Boot into Safe Mode',
      'Run an offline malware scan',
      'Uninstall the suspicious program',
      'Change all passwords (assume they were stolen)'
    ],
    myth: {
      myth: "Trojans replicate like viruses.",
      reality: "Trojans do not self-replicate. They rely on tricking a human into copying and running them."
    },
    classMode: {
      duration: '10 mins',
      discussion: ['Why are "Game Cracks" or "Free Movie Downloader" apps common Trojans?', 'How does Social Engineering play a role here?']
    },
    quiz: [
      { id: 't1', text: 'Does a Trojan self-replicate?', options: ['Yes', 'No', 'Only on Tuesdays', 'If it is a worm'], correctIndex: 1, explanation: 'Trojans rely on users to execute them; they do not self-replicate like worms.' },
      { id: 't2', text: 'How does a Trojan typically arrive?', options: ['Magic', 'Network exploit', 'Disguised as useful software', 'Physical access'], correctIndex: 2, explanation: 'It pretends to be something good (like a game or tool) to trick you.' },
      { id: 't3', text: 'What is a "Remote Access Trojan" (RAT)?', options: ['A rodent virus', 'Allows attacker remote control', 'Encrypted file', 'Adware'], correctIndex: 1, explanation: 'RATs give attackers full control over the infected machine.' }
    ]
  },
  {
    id: 'ransomware',
    title: 'Ransomware',
    definition: 'Malware that locks your computer or encrypts your files, demanding payment (ransom) to restore access.',
    entryVectors: 'Phishing emails with malicious links/attachments, or exploiting remote access tools (like RDP).',
    objectives: 'Financial extortion. Attackers may also threaten to leak stolen data ("Double Extortion").',
    warningSigns: [
      'Cannot open documents (wrong extension)',
      'Wallpaper changed to a ransom note',
      'Pop-up window with a countdown timer'
    ],
    prevention: [
      'Maintain 3-2-1 Backups (3 copies, 2 media, 1 offline)',
      'Patch software immediately to close holes',
      'Use Endpoint Detection & Response (EDR)',
      'Filter emails for malicious links',
      'Disable Remote Desktop (RDP) if not needed'
    ],
    detection: [
      'Massive number of file modifications in seconds',
      'High CPU usage during encryption',
      'Security tools alerting on "Shadow Copy" deletion'
    ],
    response: [
      'Disconnect from network immediately (stop the spread)',
      'DO NOT pay the ransom (no guarantee of data)',
      'Report to IT / Security team',
      'Wipe the infected machine',
      'Restore data from clean, offline backups'
    ],
    myth: {
      myth: "Paying the ransom guarantees I get my files back.",
      reality: "Criminals often take the money and ghost you, or send a broken decryption key. Restore from backup instead."
    },
    classMode: {
      duration: '15 mins',
      discussion: ['Should a hospital pay the ransom if patients are at risk? Why/Why not?', 'Why must backups be "offline" to be safe?']
    },
    quiz: [
      { id: 'r1', text: 'What is the main goal of ransomware?', options: ['Damage hardware', 'Financial extortion', 'Steal bandwidth', 'Display ads'], correctIndex: 1, explanation: 'Criminals use it to force victims to pay money.' },
      { id: 'r2', text: 'What is the best defence against data loss from ransomware?', options: ['Antivirus', 'Firewalls', 'Offline Backups', 'Strong passwords'], correctIndex: 2, explanation: 'If you have offline backups, you can restore data without paying.' },
      { id: 'r3', text: 'What is "Double Extortion"?', options: ['Encrypting twice', 'Asking for double money', 'Encrypting + Threatening to leak data', 'Attacking two computers'], correctIndex: 2, explanation: 'Attackers steal data before encrypting it, threatening to release it if you don\'t pay.' }
    ]
  },
  {
    id: 'spyware-keyloggers',
    title: 'Spyware / Keyloggers',
    definition: 'Software that secretly monitors user activity, records keystrokes, and gathers personal information.',
    entryVectors: 'Bundled with "free" software downloads, malicious browser extensions, or mobile apps.',
    objectives: 'Identity theft, stealing passwords/credit cards, or corporate espionage.',
    warningSigns: [
      'Typing lags or delays',
      'Mouse cursor moving on its own',
      'New toolbars appearing in your browser'
    ],
    prevention: [
      'Use Multi-Factor Authentication (MFA) everywhere',
      'Read installation screens carefully (uncheck "extras")',
      'Use a virtual keyboard for banking',
      'Review browser extensions regularly',
      'Use a privacy-focused browser'
    ],
    detection: [
      'Anti-spyware specific scans',
      'Reviewing installed programs list',
      'Checking for unknown startup items'
    ],
    response: [
      'Change passwords from a DIFFERENT, clean device',
      'Run specialized anti-spyware removal tools',
      'Contact your bank if financial data was exposed',
      'Enable MFA on all accounts',
      'Monitor accounts for suspicious activity'
    ],
    myth: {
      myth: "Incognito mode protects me from spyware.",
      reality: "Incognito only stops the browser from saving history. It does NOT stop a keylogger installed on the OS."
    },
    classMode: {
      duration: '10 mins',
      discussion: ['If a keylogger steals your password, how does MFA (like a text code) stop the hacker?']
    },
    quiz: [
      { id: 's1', text: 'What does a keylogger do?', options: ['Locks keys', 'Records keystrokes', 'Generates keys', 'Deletes keys'], correctIndex: 1, explanation: 'It records every key you press to steal passwords.' },
      { id: 's2', text: 'How can you protect accounts even if a keylogger steals your password?', options: ['Change username', 'Use MFA', 'Type faster', 'Use a mouse'], correctIndex: 1, explanation: 'Multi-Factor Authentication requires a second step (like a phone code) that keyloggers can\'t easily steal.' },
      { id: 's3', text: 'Spyware is often bundled with:', options: ['Windows updates', 'Free software/freeware', 'Hardware drivers', 'Paid antivirus'], correctIndex: 1, explanation: 'Freeware often includes "optional" offers that are actually spyware.' }
    ]
  },
  {
    id: 'rootkits',
    title: 'Rootkits',
    definition: 'A set of tools that grants an attacker administrator-level access while hiding their presence from the OS and antivirus.',
    entryVectors: 'Usually dropped by other malware (Trojans) or via direct exploits of OS vulnerabilities.',
    objectives: 'To maintain persistent, undetectable access to a system for long-term spying or control.',
    warningSigns: [
      'Antivirus software will not start',
      'Windows settings change automatically',
      'Blue Screen of Death (BSOD) errors'
    ],
    prevention: [
      'Enable "Secure Boot" in BIOS/UEFI',
      'Don\'t run daily tasks as Administrator',
      'Keep drivers and firmware updated',
      'Use Host-based Intrusion Prevention Systems (HIPS)',
      'Encrypt your hard drive (BitLocker)'
    ],
    detection: [
      'Rootkits are very hard to detect while OS is running',
      'Use an "Offline" or "Bootable" antivirus scanner',
      'Look for strange behavior despite "clean" scans'
    ],
    response: [
      'Back up critical data (files only)',
      'Wipe the hard drive completely (Format)',
      'Reinstall the Operating System from fresh media',
      'Update BIOS/Firmware',
      'Restore data and scan immediately'
    ],
    myth: {
      myth: "My antivirus says I'm clean, so I have no rootkits.",
      reality: "Rootkits can trick the OS into lying to the antivirus. Only an offline boot scan is truly reliable."
    },
    classMode: {
      duration: '10 mins',
      discussion: ['Why is a rootkit harder to detect than a normal virus?', 'What is "Secure Boot"?']
    },
    quiz: [
      { id: 'rk1', text: 'Why are rootkits dangerous?', options: ['They are fast', 'They hide deep in the OS', 'They use much RAM', 'They are expensive'], correctIndex: 1, explanation: 'They operate at a high privilege level and mask their existence from antivirus.' },
      { id: 'rk2', text: 'What is a common way to remove a kernel rootkit?', options: ['Delete the file', 'Reinstall the OS', 'Reboot', 'Run a quick scan'], correctIndex: 1, explanation: 'Because they control the OS, the only safe removal is often wiping and reinstalling.' },
      { id: 'rk3', text: 'What feature helps prevent boot-level rootkits?', options: ['Fast Boot', 'Secure Boot', 'Slow Boot', 'No Boot'], correctIndex: 1, explanation: 'Secure Boot ensures only signed, trusted software loads when the PC starts.' }
    ]
  },
  {
    id: 'botnets',
    title: 'Botnets',
    definition: 'A network of infected devices ("zombies") controlled remotely by a hacker, often without the owner knowing.',
    entryVectors: 'Guessing weak/default passwords on IoT devices (cameras, routers) or via existing malware infections.',
    objectives: 'To launch massive DDoS attacks, send spam email, or mine cryptocurrency.',
    warningSigns: [
      'Internet feels very slow',
      'Fan running high when idle (crypto mining)',
      'Friends say they received spam from you'
    ],
    prevention: [
      'Change DEFAULT passwords on all smart devices',
      'Keep router firmware updated',
      'Use a firewall',
      'Turn off devices when not in use',
      'Segment IoT devices from your main network'
    ],
    detection: [
      'Monitor router traffic for high outbound data',
      'Check if your IP is on a spam blacklist',
      'Use "Botnet removal" scanners'
    ],
    response: [
      'Disconnect the device from the internet',
      'Factory reset the device (especially IoT)',
      'Update firmware immediately after reset',
      'Set a strong, unique password',
      'Run a full malware scan on PCs'
    ],
    myth: {
      myth: "My smart fridge doesn't have data, so hackers don't want it.",
      reality: "They don't want the data; they want the processor/bandwidth to attack others."
    },
    classMode: {
      duration: '10 mins',
      discussion: ['Why do hackers target smart fridges and cameras for botnets?', 'What is a DDoS attack?']
    },
    quiz: [
      { id: 'b1', text: 'What is a "Zombie" in a botnet?', options: ['The hacker', 'The infected device', 'The antivirus', 'The internet'], correctIndex: 1, explanation: 'An infected device waiting for commands is often called a zombie.' },
      { id: 'b2', text: 'What are botnets commonly used for?', options: ['Mining crypto', 'DDoS attacks', 'Sending spam', 'All of the above'], correctIndex: 3, explanation: 'Botnets provide massive computing power for various illegal tasks.' },
      { id: 'b3', text: 'How do you protect a smart fridge from a botnet?', options: ['Unplug it', 'Change default password', 'Put ice in it', 'Use a screensaver'], correctIndex: 1, explanation: 'Many IoT devices come with "admin/admin" credentials that must be changed.' }
    ]
  }
];

export const CASE_STUDIES: CaseStudy[] = [
  {
    id: 'cs1',
    title: 'The "Urgent" Invoice',
    scenario: 'You receive an email from "accounts-payable@cornpany.com" (notice the \'r\' and \'n\') with a subject "OVERDUE INVOICE - PAYMENT REQUIRED IMMEDIATELY". Attached is a "Invoice.docm" file.',
    options: [
      { text: 'Open the attachment to check if it\'s real.', feedback: 'DANGER! .docm files contain macros. Opening it could run malicious code (Trojan/Macro malware).', isSafe: false },
      { text: 'Reply to the sender asking for clarification.', feedback: 'Risky. You confirm your email is active. Plus, the domain is spoofed (typosquatting).', isSafe: false },
      { text: 'Report to IT security and delete.', feedback: 'Correct! This is a classic phishing attempt delivering malware via macros.', isSafe: true }
    ]
  },
  {
    id: 'cs2',
    title: 'The Slow Computer',
    scenario: 'Your colleague\'s computer is running very slowly. The fan is loud. You notice a pop-up saying "Your files are encrypted" but they can still access the web browser.',
    options: [
      { text: 'Ignore it, it\'s probably just an ad.', feedback: 'Dangerous. If files are actually encrypted, ignoring it delays response.', isSafe: false },
      { text: 'Disconnect from the network (Wi-Fi/Cable) immediately.', feedback: 'Correct! This stops the ransomware from spreading to the file server or other PCs.', isSafe: true },
      { text: 'Pay the ransom immediately using the company card.', feedback: 'No! Paying encourages attacks and doesn\'t guarantee data recovery.', isSafe: false }
    ]
  },
  {
    id: 'cs3',
    title: 'The Strange USB',
    scenario: 'You find a USB drive labeled "Executive Salaries 2024" in the office parking lot.',
    options: [
      { text: 'Plug it in to see who lost it.', feedback: 'Catastrophic. USBs can carry malware that auto-executes (HID spoofing) or infects the PC immediately.', isSafe: false },
      { text: 'Take it to the IT Security team.', feedback: 'Correct. They can analyze it safely in an isolated environment.', isSafe: true },
      { text: 'Throw it in the bin.', feedback: 'Okay, but better to hand it to IT so they know someone is targeting the company.', isSafe: true }
    ]
  }
];

export const ASSESSMENT_QUESTIONS: Question[] = [
  ...MODULES.flatMap(m => m.quiz), // Include module questions
  // Add a few specific assessment ones
  { id: 'a1', text: 'What is the CIA triad in security?', options: ['Central Intelligence Agency', 'Confidentiality, Integrity, Availability', 'Computer, Internet, Access', 'Control, Inspect, Audit'], correctIndex: 1, explanation: 'CIA stands for Confidentiality, Integrity, and Availability.' },
  { id: 'a2', text: 'Which is an example of "Least Privilege"?', options: ['Giving everyone admin rights', 'Logging in as root for everything', 'Users only have access to what they need', 'No passwords'], correctIndex: 2, explanation: 'Least Privilege means giving only the necessary permissions.' },
  { id: 'a3', text: 'What does 3-2-1 backup mean?', options: ['3 copies, 2 media types, 1 offsite', '3 servers, 2 clouds, 1 person', '3 days, 2 hours, 1 minute', '3 passwords, 2 users, 1 token'], correctIndex: 0, explanation: 'The gold standard for data resilience.' }
];

export const DEFENCE_CHECKLIST = [
  { id: 'patch', title: 'Patching', desc: 'Keep OS and apps updated to fix security holes.' },
  { id: 'mfa', title: 'MFA', desc: 'Multi-Factor Authentication adds a second lock to your door.' },
  { id: 'backup', title: 'Backups (3-2-1)', desc: '3 copies, 2 media types, 1 offsite/offline.' },
  { id: 'leastpriv', title: 'Least Privilege', desc: 'Don\'t run as Admin for daily tasks.' },
  { id: 'edr', title: 'EDR / Antivirus', desc: 'Tools to detect and stop running threats.' },
  { id: 'hygiene', title: 'Email Hygiene', desc: 'Don\'t click links or open unknown attachments.' }
];

export const TEACHER_RESOURCES = {
  starter: {
    title: "10-Min Starter: 'Spot the Fake'",
    desc: "Show students 3 emails on the projector. 1 is legit, 2 are phishing. Ask them to identify red flags (Urgency, Bad Grammar, Weird URL).",
    duration: "10 mins"
  },
  mainActivity: {
    title: "Guided Activity: The Ransomware Response",
    desc: "Use the 'Case Studies' section. Run through Scenario 2 (The Slow Computer) together. discuss WHY disconnecting the network is better than turning off the PC (which might damage evidence or fail to stop network spread).",
    duration: "30 mins"
  },
  plenary: {
    title: "Plenary Questions",
    questions: [
      "What is the difference between a Virus and a Worm?",
      "Name one way to stop Ransomware without paying.",
      "Why is '123456' a bad password?"
    ],
    duration: "5 mins"
  }
};
